/**
 * KioskCheckIn.js — Smart kiosk check-in screen
 * - Shows only batches active at the current date/time
 * - Auto-selects the currently running batch
 * - "Other Batches" option for off-schedule check-ins
 * - Search by Student ID or Student Name
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FaSearch, FaCheckCircle, FaUsers, FaClock,
  FaTimesCircle, FaShieldAlt, FaLock, FaSignOutAlt,
  FaChevronDown, FaChevronUp, FaLayerGroup, FaIdCard,
  FaUserTie, FaSignInAlt,
} from "react-icons/fa";
import { Colors } from "./branding";
import {getBrandColors, darken} from "./ThemeContext";
import SixersLogo from "./Sixers_Logo.png";
import bg2        from "./Background images/2.jpg";
import API_BASE   from "./apiConfig";
import { useDateTimeFormat } from "./utils/formatters";
const BASE = API_BASE;

const T = {
  get blue() { return getBrandColors().blue; },
  get yellow() { return getBrandColors().yellow; },
  green:  "#10b981",
  red:    "#ef4444",
  gray:   "#8899aa",
  font:   "'Exo 2', 'Segoe UI', system-ui, sans-serif",
  disp:   "'Rajdhani', sans-serif",
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=Exo+2:wght@300;400;500;600;700;800&display=swap');
  @keyframes ki_hex    { 0%,100%{transform:translateY(0) rotate(0deg);opacity:0.13}   50%{transform:translateY(-18px) rotate(6deg);opacity:0.30} }
  @keyframes ki_scan   { 0%{transform:translateY(-100%)} 100%{transform:translateY(100dvh)} }
  @keyframes ki_fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
  @keyframes ki_stripe { from{transform:scaleX(0);transform-origin:left} to{transform:scaleX(1);transform-origin:left} }
  @keyframes ki_flash  { 0%{opacity:0;transform:scale(0.88)} 16%{opacity:1;transform:scale(1.04)} 78%{opacity:1;transform:scale(1)} 100%{opacity:0;transform:scale(0.97)} }
  @keyframes ki_spin   { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes ki_dot    { 0%,100%{box-shadow:0 0 5px rgba(74,222,128,0.5)} 50%{box-shadow:0 0 14px rgba(74,222,128,0.95)} }
  @keyframes ki_pulse  { 0%,100%{filter:drop-shadow(0 0 18px rgba(247,206,11,0.40))} 50%{filter:drop-shadow(0 0 38px rgba(247,206,11,0.80))} }
  @keyframes ki_ring   { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes ki_glow   { 0%,100%{box-shadow:0 0 0 0 rgba(16,185,129,0)} 50%{box-shadow:0 0 22px 4px rgba(16,185,129,0.28)} }

  .ki_batch { transition: background 0.18s, transform 0.15s, box-shadow 0.15s; }
  .ki_batch:hover { background: rgba(255,255,255,0.14) !important; transform: translateY(-2px); }
  .ki_batch_active { animation: ki_glow 2.4s ease-in-out infinite; }
  .ki_card { transition: transform 0.15s, box-shadow 0.15s; }
  .ki_card:not(.ki_done):hover { transform: translateY(-5px); box-shadow: 0 18px 44px rgba(0,0,0,0.45) !important; border-color: rgba(247,206,11,0.55) !important; }
  .ki_search::placeholder { color: rgba(255,255,255,0.25); }
  .ki_search:focus { border-color: rgba(247,206,11,0.55) !important; box-shadow: 0 0 0 3px rgba(247,206,11,0.08), 0 4px 24px rgba(0,0,0,0.30) !important; }
  .ki_other_btn { transition: background 0.18s, border-color 0.18s; }
  .ki_other_btn:hover { background: rgba(255,255,255,0.12) !important; }
`;

const HEXES = [
  { x:4,  y:8,  s:1.4, d:0.0 }, { x:17, y:54, s:0.8, d:1.2 },
  { x:61, y:9,  s:1.2, d:0.7 }, { x:80, y:61, s:1.6, d:0.5 },
  { x:44, y:77, s:0.7, d:1.8 }, { x:10, y:75, s:1.0, d:1.0 },
  { x:87, y:27, s:0.9, d:1.5 }, { x:52, y:43, s:0.6, d:2.1 },
  { x:33, y:31, s:0.55,d:0.8 }, { x:70, y:83, s:0.8, d:1.3 },
];
function HexGrid() {
  return (
    <div style={{ position:"absolute", inset:0, pointerEvents:"none", overflow:"hidden" }}>
      {HEXES.map((h, i) => (
        <div key={i} style={{
          position:"absolute", left:`${h.x}%`, top:`${h.y}%`,
          width:64*h.s, height:64*h.s,
          animation:`ki_hex ${4+h.d}s ease-in-out ${h.d}s infinite`,
        }}>
          <svg viewBox="0 0 100 100" style={{ width:"100%", height:"100%" }}>
            <polygon points="50,2 93,26 93,74 50,98 7,74 7,26"
              fill="none" stroke="rgba(247,206,11,0.48)" strokeWidth="1.5"/>
          </svg>
        </div>
      ))}
    </div>
  );
}

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

// ─── Coach Clock-In Panel ─────────────────────────────────────────────────────
function CoachClockInPanel() {
  const [coaches,  setCoaches]  = useState([]); // [{coach_id, coach_name, clockin_time|null}]
  const [saving,   setSaving]   = useState({}); // {coach_id: bool}
  const [flash,    setFlash]    = useState(null); // {name, time}

  const nowHHMM = () => {
    const n = new Date();
    return `${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`;
  };

  const load = useCallback(() => {
    const today = isoLocal(new Date());
    fetch(`${BASE}/coach-pay/rates`)
      .then(r => r.json())
      .then(async d => {
        const list = d.coaches || [];
        // Fetch today status for all coaches in parallel
        const statuses = await Promise.all(
          list.map(c =>
            fetch(`${BASE}/coach-pay/clockin/today?coach_id=${c.coach_id}`)
              .then(r => r.json())
              .then(s => ({ coach_id: c.coach_id, log: s.log || null }))
              .catch(() => ({ coach_id: c.coach_id, log: null }))
          )
        );
        const statusMap = Object.fromEntries(statuses.map(s => [s.coach_id, s.log]));
        setCoaches(list.map(c => ({ ...c, log: statusMap[c.coach_id] })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleClockIn = async (coach) => {
    const cid = coach.coach_id;
    setSaving(s => ({ ...s, [cid]: true }));
    try {
      const clockin = nowHHMM();
      const r = await fetch(`${BASE}/coach-pay/clockin`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ coach_id: cid, log_date: isoLocal(new Date()), clockin_time: clockin }),
      });
      if (!r.ok) throw new Error();
      setCoaches(prev => prev.map(c => c.coach_id === cid ? { ...c, log: { clockin_time: clockin } } : c));
      setFlash({ name: coach.coach_name, time: clockin });
      setTimeout(() => setFlash(null), 3000);
    } catch {
      setFlash({ name: null, time: null }); // error state
      setTimeout(() => setFlash(null), 2500);
    } finally { setSaving(s => ({ ...s, [cid]: false })); }
  };

  const doneCount    = coaches.filter(c => c.log).length;
  const pendingCount = coaches.filter(c => !c.log).length;

  return (
    <div style={{ width: "100%", maxWidth: 860, animation: "ki_fadeUp 0.5s ease 0.1s both" }}>

      {/* Header bar */}
      <div style={{
        background: "rgba(4,14,28,0.80)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
        border: "1px solid rgba(247,206,11,0.20)", borderRadius: 16, padding: "16px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20,
        boxShadow: "0 8px 32px rgba(0,0,0,0.30)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <FaUserTie size={18} style={{ color: T.yellow }} />
          <span style={{ fontFamily: T.disp, fontSize: 15, fontWeight: 700, color: "white", letterSpacing: "0.08em" }}>COACH CLOCK-IN</span>
          <span style={{ fontFamily: T.font, fontSize: 12, color: "rgba(255,255,255,0.40)" }}>Tap your name to record arrival</span>
        </div>
        <div style={{ display: "flex", gap: 16 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontFamily: T.disp, fontSize: 22, fontWeight: 700, color: "#10b981" }}>{doneCount}</div>
            <div style={{ fontFamily: T.disp, fontSize: 9, color: "#10b981", letterSpacing: "0.12em" }}>CLOCKED IN</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontFamily: T.disp, fontSize: 22, fontWeight: 700, color: T.yellow }}>{pendingCount}</div>
            <div style={{ fontFamily: T.disp, fontSize: 9, color: T.yellow, letterSpacing: "0.12em" }}>PENDING</div>
          </div>
        </div>
      </div>

      {/* Flash confirmation */}
      {flash && (
        <div style={{
          padding: "16px 24px", borderRadius: 14, marginBottom: 20,
          background: flash.name ? "rgba(16,185,129,0.13)" : "rgba(239,68,68,0.13)",
          border: `1.5px solid ${flash.name ? "rgba(16,185,129,0.55)" : "rgba(239,68,68,0.55)"}`,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 14,
          animation: "ki_flash 2.8s ease forwards",
        }}>
          {flash.name
            ? <><FaCheckCircle size={22} style={{ color: "#10b981" }} />
                <span style={{ fontFamily: T.disp, fontSize: 18, fontWeight: 700, letterSpacing: "0.10em", color: "#10b981" }}>
                  {flash.name} CLOCKED IN AT {flash.time}
                </span></>
            : <><FaTimesCircle size={22} style={{ color: "#ef4444" }} />
                <span style={{ fontFamily: T.disp, fontSize: 18, fontWeight: 700, color: "#ef4444" }}>CLOCK-IN FAILED</span></>
          }
        </div>
      )}

      {/* Coach cards grid */}
      {coaches.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "rgba(255,255,255,0.35)", fontFamily: T.disp, fontSize: 14, letterSpacing: "0.06em" }}>
          NO ACTIVE COACHES FOUND
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }}>
          {coaches.map(coach => {
            const done = !!coach.log;
            const busy = !!saving[coach.coach_id];
            const initials = coach.coach_name.split(" ").map(w => w[0]).slice(0, 2).join("");
            return (
              <div key={coach.coach_id}
                className={done ? "" : "ki_card"}
                onClick={() => !done && !busy && handleClockIn(coach)}
                style={{
                  padding: "24px 18px", borderRadius: 18, textAlign: "center", cursor: done ? "default" : "pointer",
                  background: done ? "rgba(16,185,129,0.10)" : "rgba(255,255,255,0.06)",
                  border: done ? "1.5px solid rgba(16,185,129,0.45)" : "1.5px solid rgba(247,206,11,0.20)",
                  backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
                  boxShadow: done ? "0 4px 24px rgba(16,185,129,0.12)" : "0 4px 20px rgba(0,0,0,0.28)",
                  transition: "all 0.18s", position: "relative", WebkitTapHighlightColor: "transparent",
                }}>

                {/* Done tick */}
                {done && (
                  <div style={{ position: "absolute", top: 10, right: 10, width: 26, height: 26, borderRadius: "50%", background: "#10b981", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 10px rgba(16,185,129,0.55)" }}>
                    <FaCheckCircle size={14} style={{ color: "white" }} />
                  </div>
                )}

                {/* Avatar */}
                <div style={{
                  width: 72, height: 72, borderRadius: "50%", margin: "0 auto 14px",
                  background: done ? "rgba(16,185,129,0.15)" : "rgba(247,206,11,0.10)",
                  border: `2.5px solid ${done ? "rgba(16,185,129,0.50)" : "rgba(247,206,11,0.30)"}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: done ? "0 0 18px rgba(16,185,129,0.25)" : "0 0 14px rgba(247,206,11,0.10)",
                }}>
                  <span style={{ fontFamily: T.disp, fontSize: 24, fontWeight: 700, color: done ? "#10b981" : T.yellow }}>
                    {initials}
                  </span>
                </div>

                {/* Name */}
                <div style={{ fontFamily: T.font, fontSize: 14, fontWeight: 700, color: done ? "#10b981" : "white", marginBottom: 10, lineHeight: 1.3 }}>
                  {coach.coach_name}
                </div>

                {/* Status / button */}
                {done ? (
                  <div style={{ fontFamily: T.disp, fontSize: 13, fontWeight: 700, color: "#10b981", letterSpacing: "0.08em" }}>
                    {coach.log.clockin_time}
                  </div>
                ) : busy ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                    <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(247,206,11,0.25)", borderTopColor: T.yellow, animation: "ki_spin 0.7s linear infinite" }} />
                    <span style={{ fontFamily: T.disp, fontSize: 11, color: T.yellow, letterSpacing: "0.10em" }}>SAVING…</span>
                  </div>
                ) : (
                  <div style={{
                    padding: "8px 18px", borderRadius: 20, display: "inline-flex", alignItems: "center", gap: 7,
                    background: `linear-gradient(135deg,${getBrandColors().yellow},${darken(getBrandColors().yellow)})`,
                    fontFamily: T.disp, fontSize: 12, fontWeight: 700, letterSpacing: "0.10em", color: getBrandColors().blueDark,
                    boxShadow: "0 3px 12px rgba(247,206,11,0.28)",
                  }}>
                    <FaSignInAlt size={11} /> CLOCK IN
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Time helpers ─────────────────────────────────────────────────────────────
function toMinutes(t) {
  if (!t) return null;
  const [h, m] = String(t).split(":").map(Number);
  return h * 60 + m;
}
// Returns "active" | "upcoming" | "past" based on current time vs batch window
function batchStatus(batch) {
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();
  const start = toMinutes(batch.start_time || batch.weekday_start);
  const end   = toMinutes(batch.end_time   || batch.weekday_end);
  if (start == null || end == null) return "active"; // no schedule data — treat as active
  if (cur >= start - 45 && cur <= end + 20) return "active";
  if (cur < start - 45) return "upcoming";
  return "past";
}

function minutesUntil(batch) {
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();
  const start = toMinutes(batch.start_time || batch.weekday_start);
  if (start == null) return null;
  return start - cur;
}

function statusLabel(batch) {
  const s = batchStatus(batch);
  if (s === "active") return { text: "ACTIVE NOW", color: T.green, bg: "rgba(16,185,129,0.15)", border: "rgba(16,185,129,0.40)" };
  if (s === "upcoming") {
    const mins = minutesUntil(batch);
    const text = mins != null && mins < 90
      ? `STARTS IN ${mins < 60 ? `${mins}M` : `${Math.round(mins/60)}H`}`
      : "UPCOMING";
    return { text, color: T.yellow, bg: "rgba(247,206,11,0.10)", border: "rgba(247,206,11,0.30)" };
  }
  return { text: "ENDED", color: "rgba(255,255,255,0.28)", bg: "rgba(255,255,255,0.04)", border: "rgba(255,255,255,0.10)" };
}

// ─── Stat pill ────────────────────────────────────────────────────────────────
function Pill({ icon: Icon, label, value, color, bg, borderColor }) {
  return (
    <div style={{
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      padding:"16px 32px", borderRadius:16, background:bg,
      border:`1px solid ${borderColor || color + "44"}`, minWidth:120,
      boxShadow:`0 4px 24px ${color}18`, fontFamily:T.font,
      backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
    }}>
      <Icon size={19} style={{ color, marginBottom:7 }}/>
      <div style={{ fontFamily:T.disp, fontSize:32, fontWeight:700, color, lineHeight:1, letterSpacing:"0.05em" }}>{value}</div>
      <div style={{ fontFamily:T.disp, fontSize:10, fontWeight:700, color, opacity:0.80, textTransform:"uppercase", letterSpacing:"0.14em", marginTop:5 }}>{label}</div>
    </div>
  );
}

// ─── Batch card ───────────────────────────────────────────────────────────────
function BatchCard({ batch, onSelect, compact }) {
  const { fmtTime } = useDateTimeFormat();
  const sl   = statusLabel(batch);
  const isActive = batchStatus(batch) === "active";
  const borderCol = batch.color_hex ? `${batch.color_hex}55` : isActive ? "rgba(16,185,129,0.40)" : "rgba(255,255,255,0.14)";

  return (
    <div
      className={`ki_batch${isActive ? " ki_batch_active" : ""}`}
      onClick={() => onSelect(batch)}
      style={{
        padding: compact ? "14px 14px" : "18px 16px",
        borderRadius:14, cursor:"pointer",
        WebkitTapHighlightColor:"transparent",
        background: isActive ? "rgba(16,185,129,0.08)" : "rgba(255,255,255,0.06)",
        border:`1.5px solid ${borderCol}`,
        boxShadow: isActive ? "0 4px 24px rgba(16,185,129,0.12)" : "0 4px 16px rgba(0,0,0,0.20)",
        position:"relative", overflow:"hidden",
      }}>
      {/* Color bar */}
      {batch.color_hex && (
        <div style={{
          position:"absolute", top:0, left:0, right:0, height:3,
          background:batch.color_hex, borderRadius:"14px 14px 0 0", opacity:0.7,
        }}/>
      )}

      {/* Status badge */}
      <div style={{
        display:"inline-flex", alignItems:"center", gap:5,
        padding:"2px 8px", borderRadius:20, marginBottom:6,
        background:sl.bg, border:`1px solid ${sl.border}`,
        fontSize:9, fontFamily:T.disp, fontWeight:700, letterSpacing:"0.10em", color:sl.color,
      }}>
        {isActive && <div style={{ width:6, height:6, borderRadius:"50%", background:T.green, flexShrink:0 }}/>}
        {sl.text}
      </div>

      <div style={{ fontFamily:T.disp, fontSize:compact ? 13 : 15, fontWeight:800, color:"white", letterSpacing:"0.04em" }}>
        {batch.batch_name}
      </div>
      <div style={{ fontSize:10, color:"rgba(255,255,255,0.48)", marginTop:2, fontFamily:T.font }}>{batch.level_name}</div>
      <div style={{ fontFamily:T.disp, fontSize:12, color:T.yellow, marginTop:5, fontWeight:600, letterSpacing:"0.06em" }}>
        {fmtTime(batch.start_time || batch.weekday_start)} – {fmtTime(batch.end_time || batch.weekday_end)}
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:5, marginTop:6 }}>
        <FaUsers size={9} style={{ color:"rgba(255,255,255,0.35)" }}/>
        <span style={{ fontFamily:T.font, fontSize:10, color:"rgba(255,255,255,0.40)" }}>
          {(batch.students_today > 0 ? batch.students_today : batch.enrolled_count) || 0} students
        </span>
      </div>
    </div>
  );
}

// ─── Student card ─────────────────────────────────────────────────────────────
function StudentCard({ student, onCheckin, loading }) {
  const initials  = student.student_name?.split(" ").map(w => w[0]).slice(0,2).join("") || "?";
  const alreadyIn = !!student.already_checked_in;
  const notToday  = student.scheduled_today === 0;

  return (
    <div
      className={`ki_card${alreadyIn ? " ki_done" : ""}`}
      onClick={() => !alreadyIn && !loading && onCheckin(student)}
      style={{
        display:"flex", flexDirection:"column", alignItems:"center",
        padding:"26px 22px", borderRadius:20,
        background:  alreadyIn ? "rgba(16,185,129,0.10)" : notToday ? "rgba(239,68,68,0.06)" : "rgba(255,255,255,0.07)",
        border:      alreadyIn ? "2px solid rgba(16,185,129,0.55)" : notToday ? "1.5px solid rgba(239,68,68,0.30)" : "1.5px solid rgba(255,255,255,0.18)",
        backdropFilter:"blur(18px)", WebkitBackdropFilter:"blur(18px)",
        cursor:      alreadyIn ? "default" : "pointer",
        boxShadow:   "0 6px 28px rgba(0,0,0,0.28)",
        position:"relative", minWidth:160, maxWidth:200,
        fontFamily:T.font, WebkitTapHighlightColor:"transparent",
        opacity: notToday && !alreadyIn ? 0.75 : 1,
      }}>

      {alreadyIn && (
        <div style={{
          position:"absolute", top:10, right:10,
          background:"#10b981", borderRadius:"50%", width:28, height:28,
          display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:"0 2px 12px rgba(16,185,129,0.55)",
        }}>
          <FaCheckCircle size={16} style={{ color:"white" }}/>
        </div>
      )}

      {notToday && !alreadyIn && (
        <div style={{
          position:"absolute", top:10, right:10,
          background:"rgba(239,68,68,0.18)", border:"1px solid rgba(239,68,68,0.45)",
          borderRadius:8, padding:"2px 7px",
          fontSize:9, fontFamily:T.disp, fontWeight:700, letterSpacing:"0.08em", color:"#fca5a5",
        }}>NOT TODAY</div>
      )}

      {/* Photo / initials */}
      <div style={{
        width:100, height:100, borderRadius:"50%", overflow:"hidden", marginBottom:14,
        border:`3px solid ${alreadyIn ? "rgba(16,185,129,0.55)" : notToday ? "rgba(239,68,68,0.30)" : "rgba(247,206,11,0.32)"}`,
        background: alreadyIn ? "rgba(16,185,129,0.12)" : notToday ? "rgba(239,68,68,0.08)" : "rgba(3,78,123,0.18)",
        display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
        boxShadow: alreadyIn ? "0 0 20px rgba(16,185,129,0.30)" : notToday ? "none" : "0 0 18px rgba(247,206,11,0.14)",
      }}>
        {student.photo_url
          ? <img src={`${BASE}/${student.photo_url}`} alt=""
              style={{ width:"100%", height:"100%", objectFit:"cover" }}
              onError={e => { e.currentTarget.style.display = "none"; }}/>
          : <span style={{
              fontFamily:T.disp, fontSize:30, fontWeight:700,
              color: alreadyIn ? "#10b981" : notToday ? "rgba(239,68,68,0.60)" : "rgba(247,206,11,0.88)",
            }}>{initials}</span>
        }
      </div>

      {/* Name */}
      {student.student_name && (
        <div style={{
          fontFamily:T.font, fontSize:13, fontWeight:600,
          color: alreadyIn ? "#10b981" : "rgba(255,255,255,0.80)", textAlign:"center",
          marginBottom:2, lineHeight:1.3,
        }}>{student.student_name}</div>
      )}

      {/* ID */}
      <div style={{
        fontFamily:T.disp, fontSize:12, fontWeight:700, letterSpacing:"0.10em",
        color: alreadyIn ? "#10b981" : "rgba(255,255,255,0.40)", textAlign:"center",
      }}>{student.student_code || `#${student.student_id}`}</div>

      {/* CTA */}
      {alreadyIn
        ? <div style={{ marginTop:10, display:"flex", alignItems:"center", gap:5, fontFamily:T.disp, fontSize:11, fontWeight:700, color:"#10b981", letterSpacing:"0.10em" }}>
            <FaCheckCircle size={11}/> CHECKED IN
          </div>
        : notToday
        ? <div style={{
            marginTop:10, fontFamily:T.disp, fontSize:11, fontWeight:700, letterSpacing:"0.08em",
            color:"#fca5a5", background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.25)",
            padding:"5px 12px", borderRadius:20,
          }}>TAP TO CHECK IN ANYWAY</div>
        : <div style={{
            marginTop:10, fontFamily:T.disp, fontSize:12, fontWeight:700, letterSpacing:"0.10em",
            color:T.yellow, background:"rgba(247,206,11,0.10)", border:"1px solid rgba(247,206,11,0.30)",
            padding:"5px 14px", borderRadius:20,
          }}>TAP TO CHECK IN</div>
      }
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN KIOSK
// ══════════════════════════════════════════════════════════════════════════════
export default function KioskCheckIn({ currentUser, onLogout }) {
  const { fmtTime } = useDateTimeFormat();
  const embedded = !onLogout;

  // ── Batch state ────────────────────────────────────────────────────────────
  const [todayBatches,    setTodayBatches]    = useState([]);
  const [allBatches,      setAllBatches]      = useState([]);
  const [loadingToday,    setLoadingToday]    = useState(true);
  const [loadingAll,      setLoadingAll]      = useState(false);
  const [showOtherPanel,  setShowOtherPanel]  = useState(false);

  // ── Active session ─────────────────────────────────────────────────────────
  const [batchId,         setBatchId]         = useState("");
  const [activeBatch,     setActiveBatch]     = useState(null);
  const [stats,           setStats]           = useState(null);

  // ── Search & check-in ─────────────────────────────────────────────────────
  const [query,           setQuery]           = useState("");
  const [results,         setResults]         = useState([]);
  const [searching,       setSearching]       = useState(false);
  const [checking,        setChecking]        = useState(false);
  const [flash,           setFlash]           = useState(null);
  const inputRef = useRef(null);

  // ── Mode: student | coach ─────────────────────────────────────────────────
  const [mode, setMode] = useState("student");

  // ── Logout modal ───────────────────────────────────────────────────────────
  const [showLogout, setShowLogout] = useState(false);
  const [logoutPwd,  setLogoutPwd]  = useState("");
  const [logoutErr,  setLogoutErr]  = useState("");
  const [logoutBusy, setLogoutBusy] = useState(false);
  const pwdRef = useRef(null);

  // ── Live clock ─────────────────────────────────────────────────────────────
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  // ── Load today's batches ───────────────────────────────────────────────────
  useEffect(() => {
    setLoadingToday(true);
    fetch(`${BASE}/batches/kiosk/today`)
      .then(r => r.json())
      .then(list => {
        // Only show batches that have at least one enrolled student
        const arr = (list || []).filter(b => (b.students_today > 0 || b.enrolled_count > 0));
        setTodayBatches(arr);
        // Auto-select: prefer the single "active now" batch
        const active = arr.filter(b => batchStatus(b) === "active");
        if (active.length === 1) {
          setBatchId(String(active[0].batch_id));
        } else if (arr.length === 1) {
          setBatchId(String(arr[0].batch_id));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingToday(false));
  }, []);

  // ── Load all batches (on demand) ───────────────────────────────────────────
  const loadAllBatches = useCallback(() => {
    if (allBatches.length > 0) { setShowOtherPanel(true); return; }
    setLoadingAll(true);
    fetch(`${BASE}/batches/kiosk/all-batches`)
      .then(r => r.json())
      .then(d => { setAllBatches((d || []).filter(b => (b.enrolled_count > 0))); setShowOtherPanel(true); })
      .catch(() => {})
      .finally(() => setLoadingAll(false));
  }, [allBatches]);

  // ── Stats & active batch ───────────────────────────────────────────────────
  const loadStats = useCallback(() => {
    if (!batchId) return;
    fetch(`${BASE}/batches/kiosk/stats?batch_id=${batchId}`)
      .then(r => r.json()).then(d => setStats(d)).catch(() => {});
  }, [batchId]);

  useEffect(() => {
    if (!batchId) { setActiveBatch(null); setStats(null); setResults([]); setQuery(""); return; }
    const all = [...todayBatches, ...allBatches];
    const b   = all.find(x => String(x.batch_id) === String(batchId));
    setActiveBatch(b || null);
    loadStats();
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [batchId, todayBatches, allBatches, loadStats]);

  // ── Search ─────────────────────────────────────────────────────────────────
  const doSearch = useCallback(async () => {
    if (!query.trim() || !batchId) return;
    setSearching(true);
    try {
      const r = await fetch(`${BASE}/batches/kiosk/search?q=${encodeURIComponent(query)}&batch_id=${batchId}`);
      const d = await r.json();
      setResults(d || []);
    } finally { setSearching(false); }
  }, [query, batchId]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(doSearch, 300);
    return () => clearTimeout(t);
  }, [query, doSearch]);

  // ── Check-in ───────────────────────────────────────────────────────────────
  const handleCheckin = async (student) => {
    if (checking) return;
    setChecking(true);
    try {
      const r = await fetch(`${BASE}/batches/kiosk/checkin`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ student_id:student.student_id, batch_id:parseInt(batchId) }),
      });
      if (r.ok) {
        setFlash({ ok:true });
        setQuery(""); setResults([]);
        loadStats();
        setTimeout(() => { setFlash(null); inputRef.current?.focus(); }, 2800);
      } else {
        setFlash({ ok:false });
        setTimeout(() => setFlash(null), 2800);
      }
    } finally { setChecking(false); }
  };

  // ── Select batch ───────────────────────────────────────────────────────────
  const selectBatch = (b) => {
    setBatchId(String(b.batch_id));
    setShowOtherPanel(false);
  };

  const clearBatch = () => {
    setBatchId(""); setActiveBatch(null); setResults([]); setQuery("");
    setShowOtherPanel(false);
  };

  // ── Logout ─────────────────────────────────────────────────────────────────
  const openLogout  = () => { setLogoutPwd(""); setLogoutErr(""); setShowLogout(true); setTimeout(() => pwdRef.current?.focus(), 80); };
  const closeLogout = () => { setShowLogout(false); setLogoutPwd(""); setLogoutErr(""); };
  const handleLogoutConfirm = async () => {
    if (!logoutPwd.trim()) { setLogoutErr("Password is required."); return; }
    setLogoutBusy(true); setLogoutErr("");
    try {
      const r = await fetch(`${BASE}/auth/login`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ email:currentUser?.email, password:logoutPwd }),
      });
      const d = await r.json();
      if (d.success) { onLogout?.(); }
      else { setLogoutErr("Incorrect password. Access denied."); setLogoutPwd(""); setTimeout(() => pwdRef.current?.focus(), 50); }
    } catch { setLogoutErr("Unable to reach server. Try again."); }
    finally { setLogoutBusy(false); }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const activeBatches   = todayBatches.filter(b => batchStatus(b) === "active");
  const upcomingBatches = todayBatches.filter(b => batchStatus(b) === "upcoming");
  const pastBatches     = todayBatches.filter(b => batchStatus(b) === "past");
  const todayStr = now.toLocaleDateString("en-US", { weekday:"long", month:"short", day:"numeric" });
  const timeStr  = now.toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit" });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>

      <div style={{
        minHeight: embedded ? "100%" : "100dvh",
        fontFamily:T.font, overflow: embedded ? "visible" : "hidden",
        position:"relative", background:"#020d18",
        display:"flex", flexDirection:"column", alignItems:"center",
      }}>
        {/* Background */}
        <div style={{ position:"absolute", inset:0, zIndex:0, backgroundImage:`url(${bg2})`, backgroundSize:"cover", backgroundPosition:"center 30%" }}/>
        <div style={{ position:"absolute", inset:0, zIndex:1, background:"linear-gradient(160deg,rgba(2,18,45,0.78) 0%,rgba(3,60,100,0.56) 48%,rgba(2,18,45,0.78) 100%)" }}/>
        <div style={{ position:"absolute", top:0, left:0, right:0, height:3, zIndex:10, background:`linear-gradient(90deg,transparent,${getBrandColors().yellow} 20%,${getBrandColors().yellow} 80%,transparent)`, animation:"ki_stripe 1.2s cubic-bezier(.4,0,.2,1) 0.2s both" }}/>
        <div style={{ position:"absolute", inset:0, zIndex:2 }}><HexGrid/></div>
        <div style={{ position:"absolute", inset:0, zIndex:3, pointerEvents:"none", overflow:"hidden" }}>
          <div style={{ position:"absolute", left:0, right:0, height:3, background:"linear-gradient(180deg,transparent,rgba(247,206,11,0.05),transparent)", animation:"ki_scan 9s linear infinite" }}/>
          <div style={{ position:"absolute", inset:0, backgroundImage:"repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.025) 2px,rgba(0,0,0,0.025) 4px)" }}/>
        </div>

        {/* Content */}
        <div style={{
          position:"relative", zIndex:6,
          width:"100%", maxWidth:980,
          padding: embedded ? "32px 24px 32px" : "32px 24px 80px",
          display:"flex", flexDirection:"column", alignItems:"center",
        }}>

          {/* Logo + Title */}
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", marginBottom:28, animation:"ki_fadeUp 0.7s ease 0.15s both" }}>
            <div style={{ position:"relative", marginBottom:16 }}>
              <div style={{ position:"absolute", top:-14, left:-14, right:-14, bottom:-14, border:"2px dashed rgba(247,206,11,0.30)", borderRadius:"50%", animation:"ki_ring 22s linear infinite" }}/>
              <div style={{ position:"absolute", top:-6, left:-6, right:-6, bottom:-6, border:"1px solid rgba(247,206,11,0.12)", borderRadius:"50%" }}/>
              <div style={{ position:"absolute", inset:-20, borderRadius:"50%", background:"radial-gradient(circle, rgba(247,206,11,0.12) 0%, transparent 70%)", pointerEvents:"none" }}/>
              <img src={SixersLogo} alt="Sixers" style={{ width:118, height:118, objectFit:"contain", position:"relative", zIndex:1, animation:"ki_pulse 3s ease-in-out infinite" }}/>
            </div>
            <h1 style={{ margin:0, fontFamily:T.disp, fontSize:34, fontWeight:700, color:"white", letterSpacing:"0.10em", textTransform:"uppercase", textShadow:"0 0 40px rgba(247,206,11,0.18)" }}>
              CHECK-IN KIOSK
            </h1>
            {/* Date + time strip */}
            <div style={{ display:"flex", alignItems:"center", gap:18, marginTop:10 }}>
              <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                <FaClock size={11} style={{ color:T.yellow }}/>
                <span style={{ fontFamily:T.disp, fontSize:11, fontWeight:700, color:T.yellow, letterSpacing:"0.12em" }}>{todayStr}</span>
              </div>
              <div style={{ width:4, height:4, borderRadius:"50%", background:"rgba(247,206,11,0.40)" }}/>
              <span style={{ fontFamily:T.disp, fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.55)", letterSpacing:"0.10em" }}>{timeStr}</span>
            </div>
          </div>

          {/* ══ MODE TOGGLE ══ */}
          <div style={{
            display: "flex", gap: 0, marginBottom: 28,
            background: "rgba(4,14,28,0.70)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
            border: "1px solid rgba(247,206,11,0.18)", borderRadius: 14, padding: 4,
            animation: "ki_fadeUp 0.5s ease 0.25s both",
          }}>
            {[
              { key: "student", label: "STUDENT CHECK-IN", Icon: FaIdCard },
              { key: "coach",   label: "COACH CLOCK-IN",   Icon: FaUserTie },
            ].map(({ key, label, Icon }) => (
              <button key={key} onClick={() => setMode(key)} style={{
                flex: 1, padding: "11px 24px", borderRadius: 10, border: "none", cursor: "pointer",
                background: mode === key ? `linear-gradient(135deg,${getBrandColors().yellow},${darken(getBrandColors().yellow)})` : "transparent",
                color: mode === key ? getBrandColors().blueDark : "rgba(255,255,255,0.45)",
                fontFamily: T.disp, fontSize: 12, fontWeight: 700, letterSpacing: "0.10em",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                transition: "all 0.18s",
                boxShadow: mode === key ? "0 3px 12px rgba(247,206,11,0.28)" : "none",
              }}>
                <Icon size={13} />{label}
              </button>
            ))}
          </div>

          {/* ══ COACH CLOCK-IN PANEL ══ */}
          {mode === "coach" && <CoachClockInPanel />}

          {/* ══ BATCH SELECTION ══ */}
          {mode === "student" && !batchId && (
            <div style={{
              background:"rgba(4,14,28,0.80)", backdropFilter:"blur(22px)", WebkitBackdropFilter:"blur(22px)",
              border:"1px solid rgba(247,206,11,0.22)", borderRadius:22, padding:"28px 28px",
              width:"100%", maxWidth:620, animation:"ki_fadeUp 0.6s ease 0.30s both",
              boxShadow:"0 28px 64px rgba(0,0,0,0.50)",
            }}>

              {/* Header */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
                <div style={{ fontFamily:T.disp, fontSize:13, fontWeight:700, color:"rgba(255,255,255,0.60)", letterSpacing:"0.14em", textTransform:"uppercase" }}>
                  Select Your Session
                </div>
                <div style={{
                  fontFamily:T.disp, fontSize:11, fontWeight:600, letterSpacing:"0.10em",
                  color:T.yellow, background:"rgba(247,206,11,0.10)", border:"1px solid rgba(247,206,11,0.25)",
                  padding:"3px 12px", borderRadius:20,
                }}>{todayStr}</div>
              </div>

              {/* Loading */}
              {loadingToday && (
                <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"32px 0", gap:12 }}>
                  <div style={{ width:20, height:20, borderRadius:"50%", border:"2.5px solid rgba(247,206,11,0.20)", borderTopColor:T.yellow, animation:"ki_spin 0.7s linear infinite" }}/>
                  <span style={{ fontFamily:T.font, fontSize:13, color:"rgba(255,255,255,0.40)" }}>Finding today's sessions…</span>
                </div>
              )}

              {/* No batches */}
              {!loadingToday && todayBatches.length === 0 && !showOtherPanel && (
                <div style={{ textAlign:"center", padding:"24px 0 12px" }}>
                  <FaClock size={32} style={{ color:"rgba(247,206,11,0.25)", display:"block", margin:"0 auto 12px" }}/>
                  <div style={{ fontFamily:T.disp, fontSize:14, color:"rgba(255,255,255,0.40)", letterSpacing:"0.06em" }}>No sessions scheduled for today</div>
                  <div style={{ fontFamily:T.font, fontSize:11, color:"rgba(255,255,255,0.25)", marginTop:5 }}>Check back on your next training day</div>
                </div>
              )}

              {/* Active Now */}
              {!loadingToday && activeBatches.length > 0 && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:T.green, boxShadow:"0 0 8px rgba(16,185,129,0.70)" }}/>
                    <span style={{ fontFamily:T.disp, fontSize:11, fontWeight:700, color:T.green, letterSpacing:"0.14em" }}>ACTIVE NOW</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns: activeBatches.length === 1 ? "1fr" : "1fr 1fr", gap:10 }}>
                    {activeBatches.map(b => <BatchCard key={b.batch_id} batch={b} onSelect={selectBatch}/>)}
                  </div>
                </div>
              )}

              {/* Upcoming */}
              {!loadingToday && upcomingBatches.length > 0 && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:T.yellow }}/>
                    <span style={{ fontFamily:T.disp, fontSize:11, fontWeight:700, color:T.yellow, letterSpacing:"0.14em" }}>UPCOMING TODAY</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns: upcomingBatches.length === 1 ? "1fr" : "1fr 1fr", gap:10 }}>
                    {upcomingBatches.map(b => <BatchCard key={b.batch_id} batch={b} onSelect={selectBatch}/>)}
                  </div>
                </div>
              )}

              {/* Past (collapsed by default if there are active/upcoming) */}
              {!loadingToday && pastBatches.length > 0 && (activeBatches.length > 0 || upcomingBatches.length > 0) && (
                <div style={{ marginBottom:12 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:"rgba(255,255,255,0.25)" }}/>
                    <span style={{ fontFamily:T.disp, fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.35)", letterSpacing:"0.14em" }}>EARLIER TODAY</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    {pastBatches.map(b => <BatchCard key={b.batch_id} batch={b} onSelect={selectBatch} compact/>)}
                  </div>
                </div>
              )}

              {/* Divider */}
              {!loadingToday && <div style={{ height:1, background:"rgba(255,255,255,0.06)", margin:"12px 0" }}/>}

              {/* Other Batches toggle */}
              {!loadingToday && (
                <button
                  className="ki_other_btn"
                  onClick={() => showOtherPanel ? setShowOtherPanel(false) : loadAllBatches()}
                  style={{
                    width:"100%", padding:"11px 16px", borderRadius:12,
                    border:"1px solid rgba(255,255,255,0.12)",
                    background:"rgba(255,255,255,0.04)", cursor:"pointer",
                    WebkitTapHighlightColor:"transparent",
                    display:"flex", alignItems:"center", justifyContent:"center", gap:9,
                    fontFamily:T.disp, fontSize:12, fontWeight:700, letterSpacing:"0.10em",
                    color:"rgba(255,255,255,0.50)",
                  }}>
                  {loadingAll
                    ? <><div style={{ width:14, height:14, borderRadius:"50%", border:"2px solid rgba(255,255,255,0.2)", borderTopColor:"white", animation:"ki_spin 0.7s linear infinite" }}/> LOADING…</>
                    : showOtherPanel
                    ? <><FaChevronUp size={11}/> HIDE OTHER BATCHES</>
                    : <><FaLayerGroup size={11}/> BROWSE OTHER BATCHES</>
                  }
                </button>
              )}

              {/* All batches panel */}
              {showOtherPanel && allBatches.length > 0 && (
                <div style={{ marginTop:14 }}>
                  <div style={{ fontFamily:T.disp, fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.38)", letterSpacing:"0.12em", marginBottom:10 }}>
                    ALL ACTIVE BATCHES
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    {allBatches.map(b => <BatchCard key={b.batch_id} batch={b} onSelect={selectBatch} compact/>)}
                  </div>
                </div>
              )}

            </div>
          )}

          {/* ══ ACTIVE CHECK-IN AREA ══ */}
          {mode === "student" && batchId && activeBatch && (
            <div style={{ width:"100%", animation:"ki_fadeUp 0.5s ease 0.1s both" }}>

              {/* Batch info bar */}
              <div style={{
                background:"rgba(4,14,28,0.72)", backdropFilter:"blur(18px)", WebkitBackdropFilter:"blur(18px)",
                border:"1px solid rgba(247,206,11,0.20)", borderRadius:16, padding:"14px 20px", marginBottom:20,
                display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
                boxShadow:"0 8px 32px rgba(0,0,0,0.30)",
              }}>
                <div style={{ minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                    <div style={{ fontFamily:T.disp, fontSize:17, fontWeight:700, color:"white", letterSpacing:"0.06em" }}>{activeBatch.batch_name}</div>
                    {/* Live status badge */}
                    {(() => {
                      const sl = statusLabel(activeBatch);
                      return (
                        <div style={{
                          display:"inline-flex", alignItems:"center", gap:4,
                          padding:"2px 9px", borderRadius:20,
                          background:sl.bg, border:`1px solid ${sl.border}`,
                          fontSize:9, fontFamily:T.disp, fontWeight:700, letterSpacing:"0.10em", color:sl.color,
                        }}>
                          {batchStatus(activeBatch) === "active" && <div style={{ width:5, height:5, borderRadius:"50%", background:T.green }}/>}
                          {sl.text}
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.46)", marginTop:3, fontFamily:T.font }}>
                    {activeBatch.level_name} · {fmtTime(activeBatch.start_time || activeBatch.weekday_start)} – {fmtTime(activeBatch.end_time || activeBatch.weekday_end)}
                    {activeBatch.students_today > 0 && (
                      <span style={{ marginLeft:8, color:T.yellow }}>· {activeBatch.students_today} scheduled today</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={clearBatch}
                  style={{
                    flexShrink:0,
                    background:"rgba(247,206,11,0.10)", border:"1px solid rgba(247,206,11,0.30)",
                    color:T.yellow, borderRadius:10, padding:"8px 18px", cursor:"pointer",
                    WebkitTapHighlightColor:"transparent",
                    fontFamily:T.disp, fontSize:11, fontWeight:700, letterSpacing:"0.10em",
                    transition:"background 0.15s",
                  }}>CHANGE BATCH</button>
              </div>

              {/* Stats */}
              {stats && (
                <div style={{ display:"flex", gap:14, justifyContent:"center", marginBottom:22, flexWrap:"wrap" }}>
                  <Pill icon={FaUsers}       label="Total"      value={stats.total}      color="rgba(255,255,255,0.76)" bg="rgba(255,255,255,0.06)" borderColor="rgba(255,255,255,0.14)"/>
                  <Pill icon={FaCheckCircle} label="Checked In" value={stats.checked_in} color="#10b981"                bg="rgba(16,185,129,0.10)"  borderColor="rgba(16,185,129,0.35)"/>
                  <Pill icon={FaClock}       label="Remaining"  value={stats.remaining}  color={T.yellow}               bg="rgba(247,206,11,0.09)"  borderColor="rgba(247,206,11,0.30)"/>
                </div>
              )}

              {/* Flash */}
              {flash && (
                <div style={{
                  padding:"24px 36px", borderRadius:16, marginBottom:22,
                  background: flash.ok ? "rgba(16,185,129,0.13)" : "rgba(239,68,68,0.13)",
                  border:`1.5px solid ${flash.ok ? "rgba(16,185,129,0.55)" : "rgba(239,68,68,0.55)"}`,
                  backdropFilter:"blur(18px)", WebkitBackdropFilter:"blur(18px)",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:18,
                  animation:"ki_flash 2.8s ease forwards",
                  boxShadow: flash.ok ? "0 10px 40px rgba(16,185,129,0.22)" : "0 10px 40px rgba(239,68,68,0.18)",
                }}>
                  {flash.ok
                    ? <FaCheckCircle size={32} style={{ color:"#10b981", filter:"drop-shadow(0 0 10px rgba(16,185,129,0.70))", flexShrink:0 }}/>
                    : <FaTimesCircle size={32} style={{ color:"#ef4444", flexShrink:0 }}/>}
                  <div style={{ fontFamily:T.disp, fontSize:26, fontWeight:700, letterSpacing:"0.12em", color: flash.ok ? "#10b981" : "#ef4444" }}>
                    {flash.ok ? "CHECK-IN SUCCESSFUL" : "CHECK-IN FAILED"}
                  </div>
                </div>
              )}

              {/* Search — by ID or Name */}
              <div style={{ marginBottom:10 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 12px", borderRadius:20, background:"rgba(247,206,11,0.08)", border:"1px solid rgba(247,206,11,0.20)" }}>
                    <FaIdCard size={10} style={{ color:T.yellow }}/>
                    <span style={{ fontFamily:T.disp, fontSize:10, fontWeight:700, color:T.yellow, letterSpacing:"0.10em" }}>STUDENT ID</span>
                  </div>
                  <span style={{ fontFamily:T.disp, fontSize:10, color:"rgba(255,255,255,0.25)", letterSpacing:"0.08em" }}>OR</span>
                  <div style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 12px", borderRadius:20, background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)" }}>
                    <FaSearch size={9} style={{ color:"rgba(255,255,255,0.40)" }}/>
                    <span style={{ fontFamily:T.disp, fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.40)", letterSpacing:"0.10em" }}>STUDENT NAME</span>
                  </div>
                </div>

                <div style={{ position:"relative" }}>
                  <FaSearch style={{ position:"absolute", left:20, top:"50%", transform:"translateY(-50%)", color:"rgba(247,206,11,0.50)", fontSize:18 }}/>
                  <input
                    className="ki_search"
                    ref={inputRef}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && doSearch()}
                    placeholder="Enter Student ID (e.g. S001) or Name…"
                    style={{
                      width:"100%", padding:"18px 18px 18px 56px",
                      borderRadius:16, border:"1.5px solid rgba(247,206,11,0.22)",
                      background:"rgba(4,14,28,0.68)",
                      backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
                      color:"white", fontSize:18, fontFamily:T.font,
                      boxSizing:"border-box", outline:"none",
                      boxShadow:"0 4px 28px rgba(0,0,0,0.32)",
                      transition:"border-color 0.2s, box-shadow 0.2s",
                    }}/>
                  {searching && (
                    <div style={{ position:"absolute", right:20, top:"50%", transform:"translateY(-50%)", width:18, height:18, borderRadius:"50%", border:"2.5px solid rgba(247,206,11,0.22)", borderTopColor:T.yellow, animation:"ki_spin 0.7s linear infinite" }}/>
                  )}
                </div>
              </div>

              {/* Results */}
              {results.length > 0 && (
                <div style={{ display:"flex", gap:16, flexWrap:"wrap", justifyContent:"center", marginTop:16 }}>
                  {results.map(s => (
                    <StudentCard key={s.student_id} student={s} onCheckin={handleCheckin} loading={checking}/>
                  ))}
                </div>
              )}

              {/* No results */}
              {query && results.length === 0 && !searching && (
                <div style={{ textAlign:"center", color:"rgba(255,255,255,0.38)", fontSize:15, padding:40, fontFamily:T.font }}>
                  No students found for "<strong style={{ color:"rgba(255,255,255,0.55)" }}>{query}</strong>" in this batch.
                </div>
              )}

              {/* Idle prompt */}
              {!query && !flash && (
                <div style={{ textAlign:"center", padding:"44px 24px" }}>
                  <FaIdCard size={36} style={{ color:"rgba(247,206,11,0.15)", display:"block", margin:"0 auto 14px" }}/>
                  <p style={{ color:"rgba(255,255,255,0.28)", fontSize:14, fontFamily:T.font, margin:0, letterSpacing:"0.03em" }}>
                    Enter your Student ID or Name above to check in
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Privacy notice */}
          <div style={{ marginTop:40, width:"100%", display:"flex", justifyContent:"center" }}>
            <div style={{
              display:"flex", alignItems:"flex-start", gap:13,
              background:"rgba(4,14,28,0.62)", backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
              border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:"12px 22px",
              width:"100%", maxWidth:900, boxShadow:"0 4px 24px rgba(0,0,0,0.22)",
            }}>
              <FaShieldAlt size={15} style={{ color:"rgba(247,206,11,0.48)", marginTop:2, flexShrink:0 }}/>
              <div style={{ fontSize:11, lineHeight:1.75, color:"rgba(255,255,255,0.32)", fontStyle:"italic", fontFamily:T.font }}>
                <strong style={{ fontStyle:"normal", fontWeight:700, color:"rgba(255,255,255,0.50)" }}>Privacy Notice: </strong>
                Participant names are not displayed on this kiosk in compliance with applicable privacy and data-protection obligations.
                Identity is verified through the assigned Student ID or name. Unauthorised access to another individual's record is strictly prohibited.
              </div>
            </div>
          </div>

        </div>

        {/* Status bar — full-screen kiosk only */}
        {!embedded && (
          <div style={{
            position:"fixed", bottom:0, left:0, right:0, zIndex:10,
            height:34, display:"flex", alignItems:"center", padding:"0 20px", gap:16,
            background:"rgba(0,0,0,0.75)", borderTop:"1px solid rgba(247,206,11,0.09)",
            backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)",
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:7, height:7, borderRadius:"50%", background:"#4ade80", animation:"ki_dot 2s ease-in-out infinite" }}/>
              <span style={{ fontFamily:T.disp, fontSize:11, color:"rgba(255,255,255,0.38)", letterSpacing:"0.12em" }}>KIOSK ONLINE</span>
            </div>
            <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.05)" }}/>
            <span style={{ fontFamily:T.disp, fontSize:11, color:"rgba(255,255,255,0.55)", letterSpacing:"0.08em" }}>
              SIXERS SPORTS CENTER · SECURE CHECK-IN KIOSK
            </span>
            {onLogout && (
              <button onClick={openLogout} style={{
                marginLeft:12, display:"flex", alignItems:"center", gap:6,
                background:"rgba(239,68,68,0.10)", border:"1px solid rgba(239,68,68,0.28)",
                color:"rgba(239,68,68,0.70)", borderRadius:7, padding:"4px 12px",
                cursor:"pointer", fontFamily:T.disp, fontSize:10, fontWeight:700,
                letterSpacing:"0.10em", WebkitTapHighlightColor:"transparent",
                transition:"background 0.15s",
              }}>
                <FaSignOutAlt size={10}/> STAFF EXIT
              </button>
            )}
          </div>
        )}

        {/* Logout modal */}
        {showLogout && (
          <div style={{
            position:"fixed", inset:0, zIndex:200,
            background:"rgba(2,13,24,0.88)", backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)",
            display:"flex", alignItems:"center", justifyContent:"center",
          }} onClick={e => { if (e.target === e.currentTarget) closeLogout(); }}>
            <div style={{
              background:"rgba(4,14,28,0.95)", border:"1px solid rgba(247,206,11,0.30)",
              borderRadius:18, padding:"36px 40px", width:"100%", maxWidth:400,
              boxShadow:"0 32px 80px rgba(0,0,0,0.70)", position:"relative",
              animation:"ki_fadeUp 0.25s ease both",
            }}>
              {[["top","left"],["top","right"],["bottom","left"],["bottom","right"]].map(([v,h],i) => (
                <div key={i} style={{ position:"absolute", [v]:-1, [h]:-1, width:22, height:22 }}>
                  <div style={{ position:"absolute", [v]:0, [h]:0, width:22, height:2.5, background:T.yellow, borderRadius:2 }}/>
                  <div style={{ position:"absolute", [v]:0, [h]:0, width:2.5, height:22, background:T.yellow, borderRadius:2 }}/>
                </div>
              ))}
              <div style={{ textAlign:"center", marginBottom:24 }}>
                <div style={{ width:56, height:56, borderRadius:"50%", margin:"0 auto 14px", background:"rgba(247,206,11,0.10)", border:"1px solid rgba(247,206,11,0.30)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <FaShieldAlt size={24} style={{ color:T.yellow }}/>
                </div>
                <div style={{ fontFamily:T.disp, fontSize:22, fontWeight:700, color:"white", letterSpacing:"0.08em" }}>SECURE LOGOUT</div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.42)", marginTop:6, fontFamily:T.font }}>Enter your password to end this kiosk session</div>
              </div>
              {logoutErr && (
                <div style={{ padding:"10px 16px", borderRadius:9, marginBottom:16, background:"rgba(239,68,68,0.12)", border:"1px solid rgba(239,68,68,0.40)", color:"#fca5a5", fontSize:13, fontFamily:T.font, display:"flex", alignItems:"center", gap:8 }}>
                  <FaTimesCircle size={13}/>{logoutErr}
                </div>
              )}
              <div style={{ position:"relative", marginBottom:20 }}>
                <FaLock style={{ position:"absolute", left:16, top:"50%", transform:"translateY(-50%)", color:"rgba(247,206,11,0.45)", fontSize:14 }}/>
                <input
                  ref={pwdRef} type="password" value={logoutPwd}
                  onChange={e => { setLogoutPwd(e.target.value); setLogoutErr(""); }}
                  onKeyDown={e => e.key === "Enter" && handleLogoutConfirm()}
                  placeholder="Enter your password…"
                  style={{ width:"100%", padding:"14px 16px 14px 48px", borderRadius:12, border:"1.5px solid rgba(247,206,11,0.25)", background:"rgba(255,255,255,0.05)", color:"white", fontSize:16, fontFamily:T.font, boxSizing:"border-box", outline:"none" }}
                />
              </div>
              <div style={{ display:"flex", gap:12 }}>
                <button onClick={closeLogout} style={{ flex:1, padding:"13px", borderRadius:10, border:"1px solid rgba(255,255,255,0.15)", background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.65)", fontFamily:T.disp, fontSize:13, fontWeight:700, letterSpacing:"0.08em", cursor:"pointer" }}>CANCEL</button>
                <button onClick={handleLogoutConfirm} disabled={logoutBusy} style={{ flex:1, padding:"13px", borderRadius:10, border:"none", background:logoutBusy ? "rgba(247,206,11,0.40)" : `linear-gradient(135deg,${getBrandColors().yellow},${darken(getBrandColors().yellow)})`, color:getBrandColors().blueDark, fontFamily:T.disp, fontSize:13, fontWeight:700, letterSpacing:"0.08em", cursor:logoutBusy ? "default" : "pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                  {logoutBusy ? "VERIFYING…" : <><FaSignOutAlt size={13}/>CONFIRM EXIT</>}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}
